/**
 * The one access-decision walk (design.md C2, ADR-6). It runs on plain data so the Lambda handlers
 * and the SPA share a single implementation; `allows()` in the handlers is a thin wrapper.
 *
 * Order (reproduces `dbo.fnSecurityEntry_HasAccess` and legacy's `HasAccessByPath` /
 * `IsInactivatedByPath`):
 *  1. Inactivated at the path or any ancestor denies, ahead of the System Administrator bypass.
 *     A re-activation row (level 1) at a level stops the upward inactivation walk without denying.
 *  2. A System Administrator is allowed.
 *  3. Walk from the path to the root. At each level pool the caller's own roles with Everyone:
 *     any Allow wins over any Deny at that level, and the first level holding any row decides.
 *  4. No row anywhere in the chain denies.
 *
 * Comparison (`ruleKey`) is the comparison SQL Server's `=` makes on the `varchar` column under a
 * case-insensitive collation: trailing spaces are not significant (ANSI padding) and letter case is
 * not significant. Both the stored rule paths and every walked level are keyed this way, so a stored
 * `X\Y ` row applies to a request for `X\Y` exactly as it does in the function (red-team F-3; the
 * local database holds such a row: an inactivation with a trailing space).
 *
 * A requested path the function itself cannot walk correctly is refused before any lookup
 * (`unwalkable`): its parent derivation `LEFT(path, LEN(path) - CHARINDEX('\', REVERSE(path)))` uses
 * `LEN`, which ignores trailing spaces, so a level ending in a space yields a wrong parent (for
 * `A\B \C` the next level is `''`, not `A`). Rather than reproduce that, such a path is denied;
 * likewise a path over 700 characters (the parameter is `varchar(700)` and is truncated) and a path
 * holding anything but printable ASCII (`toLowerCase` and the collation can disagree). Every
 * registry path is printable ASCII with no padded segment (`pathProblem`), so no declared
 * permission is affected; the refusal can only remove access, never add it.
 *
 * Parent derivation is otherwise `lastIndexOf('\\') > 0` with no normalisation, matching the
 * function's own walk for every other input (red-team run: 672,250 comparisons, zero differences).
 */
import { MAX_PATH_LENGTH, PATH_SEPARATOR } from './path-rules';

/** A rule set: a `Set`, array or any iterable of stored paths. */
type PathSet = Iterable<string>;

/**
 * The data `evaluateAccess` needs. Field names match `EffectivePermissionsSnapshot`, so a server
 * snapshot can be passed directly. The effective-permissions **wire** shape cannot: its
 * `permissions` is `{ code, securityType }[]`, and a non-string member is ignored (fail closed), so
 * passing it directly would silently drop every own-role Allow. Convert it with
 * `accessRulesFromWire` (red-team F-4).
 */
export interface AccessRules {
  /** Paths explicitly allowed to one of the caller's own roles. */
  permissions: PathSet;
  /** Paths explicitly denied to one of the caller's own roles. */
  denials: PathSet;
  /** Paths allowed to the Everyone role. */
  everyoneAllows: PathSet;
  /** Paths denied to the Everyone role. */
  everyoneDenials: PathSet;
  /** Paths inactivated system-wide (`SourceId = Guid.Empty`, level 0). */
  inactivatedPaths: PathSet;
  /** Paths explicitly re-activated (`SourceId = Guid.Empty`, level 1). */
  reactivatedPaths: PathSet;
  /** System Administrator bypass, applied after inactivation. */
  isSystemAdmin: boolean;
}

/**
 * The effective-permissions response as decoded from JSON (`EffectivePermissions` in
 * `@ic/platform-contract`), declared structurally so this package keeps zero dependencies. Every
 * rule set is optional: an older backend omits them.
 */
export interface WireAccessRules {
  permissions?: ReadonlyArray<{ readonly code: string }> | null;
  denials?: readonly string[] | null;
  everyoneAllows?: readonly string[] | null;
  everyoneDenials?: readonly string[] | null;
  inactivatedPaths?: readonly string[] | null;
  reactivatedPaths?: readonly string[] | null;
  isSystemAdmin?: boolean | null;
}

/**
 * Maps the wire shape to `AccessRules`: `permissions` becomes its `code` strings, a missing rule set
 * becomes empty, and only a literal `true` grants the System Administrator bypass. A member that is
 * not of the declared shape is dropped (it can only remove an Allow, never add one).
 */
export function accessRulesFromWire(wire: WireAccessRules): AccessRules {
  const codes: string[] = [];
  for (const p of wire.permissions ?? []) {
    const code: unknown = (p as { code?: unknown } | null)?.code;
    if (typeof code === 'string') codes.push(code);
  }
  return {
    permissions: codes,
    denials: stringsOf(wire.denials),
    everyoneAllows: stringsOf(wire.everyoneAllows),
    everyoneDenials: stringsOf(wire.everyoneDenials),
    inactivatedPaths: stringsOf(wire.inactivatedPaths),
    reactivatedPaths: stringsOf(wire.reactivatedPaths),
    isSystemAdmin: wire.isSystemAdmin === true,
  };
}

function stringsOf(values: readonly unknown[] | null | undefined): string[] {
  return (values ?? []).filter((v): v is string => typeof v === 'string');
}

interface KeyedRules {
  permissions: Set<string>;
  denials: Set<string>;
  everyoneAllows: Set<string>;
  everyoneDenials: Set<string>;
  inactivatedPaths: Set<string>;
  reactivatedPaths: Set<string>;
}

/**
 * The comparison key SQL Server's `=` uses here: trailing spaces dropped, case folded. Exported (as
 * `accessRuleKey`) so a display that explains a decision (Security Controls' Applied Rights / Decided
 * By and the tree's inactive marker) matches stored rows exactly as this walk does (SS2, ADR-10).
 */
export function ruleKey(path: string): string {
  // A linear scan, not `/ +$/`: that regex backtracks quadratically on long runs of interior spaces (Sonar S8786).
  let end = path.length;
  while (end > 0 && path.codePointAt(end - 1) === 0x20) end -= 1;
  return path.slice(0, end).toLowerCase();
}

const PRINTABLE_ASCII = /^[ -~]+$/;
/** A level ending in a space: a space before a separator, or at the end of the path. */
const PADDED_LEVEL_END = / (\\|$)/;

/** True when the function's own walk is malformed for `path` (see the module comment). */
function unwalkable(path: string): boolean {
  return path.length > MAX_PATH_LENGTH || !PRINTABLE_ASCII.test(path) || PADDED_LEVEL_END.test(path);
}

interface KeyedSource {
  /** The source's members, in iteration order, when it was keyed. */
  members: readonly unknown[];
  keys: Set<string>;
}

/**
 * Keyed copies cached per source `Set` / array **object** and validated against its content on
 * every use (red-team F-7): the cache is reused only while the source still holds exactly the same
 * members in the same order, so an in-place replacement, a deletion, or a new same-length array is
 * re-keyed, never served stale. Validation is a reference comparison per member, cheaper than
 * re-keying. Keying on the source sets (not the rules object) also lets `{ ...snapshot }` copies,
 * as `allowsIgnoringAdmin` makes per call, share one keyed copy.
 */
const keyedSources = new WeakMap<object, KeyedSource>();

function sameMembers(source: Set<unknown> | readonly unknown[], members: readonly unknown[]): boolean {
  const size = source instanceof Set ? source.size : source.length;
  if (size !== members.length) return false;
  let i = 0;
  for (const member of source) {
    if (member !== members[i]) return false;
    i += 1;
  }
  return true;
}

function keysOf(members: Iterable<unknown>): Set<string> {
  const keys = new Set<string>();
  for (const path of members) {
    if (typeof path === 'string') keys.add(ruleKey(path));
  }
  return keys;
}

/**
 * An absent rule set (an older effective-permissions response, or a partially built object in a
 * test) holds no rows. Deny rows are the only thing that can be lost this way, and the server
 * always builds every set, so this tolerance is for the SPA's older-backend fallback and tests.
 * Any other iterable (possibly one-shot) is keyed afresh on every call and never cached.
 */
export function keySet(source: PathSet | undefined | null): Set<string> {
  if (source === undefined || source === null) return new Set<string>();
  if (!(source instanceof Set) && !Array.isArray(source)) return keysOf(source);
  const hit = keyedSources.get(source);
  if (hit && sameMembers(source, hit.members)) return hit.keys;
  const members: unknown[] = [...source];
  const keys = keysOf(members);
  keyedSources.set(source, { members, keys });
  return keys;
}

function keyed(rules: AccessRules): KeyedRules {
  return {
    permissions: keySet(rules.permissions),
    denials: keySet(rules.denials),
    everyoneAllows: keySet(rules.everyoneAllows),
    everyoneDenials: keySet(rules.everyoneDenials),
    inactivatedPaths: keySet(rules.inactivatedPaths),
    reactivatedPaths: keySet(rules.reactivatedPaths),
  };
}

/** The path with its last segment removed, or `null` at the root (the proc's own derivation). */
function parentOf(path: string): string | null {
  const i = path.lastIndexOf(PATH_SEPARATOR);
  return i > 0 ? path.slice(0, i) : null;
}

function inactivated(rules: KeyedRules, path: string): boolean {
  let level: string | null = path;
  while (level) {
    const key = ruleKey(level);
    // Deny first, so a path carrying both rows stays hidden.
    if (rules.inactivatedPaths.has(key)) return true;
    if (rules.reactivatedPaths.has(key)) return false;
    level = parentOf(level);
  }
  return false;
}

/**
 * Whether a caller holding `rules` may access `path`. A non-string or empty path is never allowed,
 * nor is a path the function cannot walk (see the module comment).
 * Pure: no I/O, no clock, no mutation of `rules`.
 */
export function evaluateAccess(rules: AccessRules, path: string): boolean {
  if (typeof path !== 'string' || path.length === 0 || unwalkable(path)) return false;
  const data = keyed(rules);
  if (inactivated(data, path)) return false;
  if (rules.isSystemAdmin === true) return true;

  let level: string | null = path;
  while (level) {
    const key = ruleKey(level);
    if (data.permissions.has(key) || data.everyoneAllows.has(key)) return true;
    if (data.denials.has(key) || data.everyoneDenials.has(key)) return false;
    level = parentOf(level);
  }
  return false;
}
